import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { Skeleton } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faCar,
  faChevronDown,
  faCircleExclamation,
  faClockRotateLeft,
  faHotel,
  faMagnifyingGlass,
  faPersonHiking,
  faSuitcase,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';

import { ITravelHistory } from '@share/common-types';
import { getTravelHistory, travelHistoryActions } from '@share/store/slices';
import { FunctionalRootState, RootState } from '@share/utils';

import { parseApiDate } from '../../utils';
import { AccountEmptyState } from '../empty-state';
// Same product pills and count as My bookings.
import filterStyles from '../bookings/styles.module.scss';

import styles from './styles.module.scss';

const OPEN_YEARS_BY_DEFAULT = 2;
const DAY_MS = 86400000;

export type YearKey = 'all' | number;
type ProductKey = '' | 'hotel' | 'condo' | 'car' | 'activity';

const PRODUCTS: { key: ProductKey; labelId: string }[] = [
  { key: '', labelId: 'my.account.hub.bookings.product.all' },
  { key: 'hotel', labelId: 'my.account.hub.bookings.product.hotels' },
  { key: 'condo', labelId: 'my.account.hub.bookings.product.condos' },
  { key: 'car', labelId: 'my.account.hub.bookings.product.cars' },
  { key: 'activity', labelId: 'my.account.hub.bookings.product.activities' },
];

const CATEGORY_ICONS: Record<string, IconDefinition> = {
  hotel: faHotel,
  condo: faBuilding,
  car: faCar,
  activity: faPersonHiking,
};

interface ITripRow {
  key: string;
  history: ITravelHistory;
  category: string;
  start: Date | null;
  end: Date | null;
  year: number | null;
}

interface IYearGroup {
  year: number | null;
  rows: ITripRow[];
}

interface IProps {
  year: YearKey;
  onYearChange: (year: YearKey) => void;
}

const normalize = (value: string | null | undefined): string => (value || '').trim().toLowerCase();

const getStatusTone = (status: string): string => {
  const value = normalize(status);
  if (value.startsWith('cancel')) {
    return 'danger';
  }
  if (value.includes('pending') || value.includes('hold')) {
    return 'warning';
  }
  if (value.includes('confirm') || value.includes('complete') || value.includes('traveled')) {
    return 'ok';
  }
  return 'neutral';
};

const getYear = (history: ITravelHistory): number | null => {
  const date = parseApiDate(history.travelStartDate) || parseApiDate(history.creationDate);
  if (date) {
    return date.getFullYear();
  }
  const membershipYear = parseInt(history.membershipYear, 10);
  return Number.isFinite(membershipYear) ? membershipYear : null;
};

/**
 * The member's past trips as a record book: one row per confirmation, grouped by year. Replaces the
 * legacy table inside the hub only; same data (`getTravelHistory`). Rows stay factual: the API's
 * destination is just the booking source and most rows have no details to open.
 */
export const AccountTripList = ({ year, onYearChange }: IProps): React.ReactElement => {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();
  const user = useSelector((state: FunctionalRootState) => state.loginStore.user);
  const travelHistoryStore = useSelector((state: FunctionalRootState) => state.travelHistoryStore);
  const { loading, error, result } = travelHistoryStore;
  const histories: ITravelHistory[] = result?.histories || [];

  const [product, setProduct] = useState<ProductKey>('');
  // Years the member opened or closed; the rest follow the default (latest two open).
  const [toggledYears, setToggledYears] = useState<Record<string, boolean>>({});
  const [requested, setRequested] = useState(false);

  const keyid = user?.keyid;

  // The slice keeps its last error, so clear it before each request.
  const load = useCallback(() => {
    dispatch(travelHistoryActions.setError(''));
    dispatch(getTravelHistory());
    setRequested(true);
  }, [dispatch]);

  useEffect(() => {
    if (keyid) {
      load();
    }
  }, [keyid, load]);

  // The API nests bookings under a parent row; every confirmation number is its own trip, so flatten
  // parents and children into one list (deduped by confirmation) and date each trip by its own start.
  const flatHistories = useMemo<ITravelHistory[]>(() => {
    const seen = new Set<string>();
    const flat: ITravelHistory[] = [];
    const add = (history: ITravelHistory) => {
      const code = normalize(history.confirmationCode);
      if (code && seen.has(code)) {
        return;
      }
      if (code) {
        seen.add(code);
      }
      flat.push({ ...history, children: [] });
    };
    histories.forEach(history => {
      add(history);
      (history.children || []).forEach(add);
    });
    return flat;
  }, [histories]);

  const rows = useMemo<ITripRow[]>(() => flatHistories.map((history, index) => {
    return {
      key: `${history.confirmationCode || 'trip'}-${index}`,
      history,
      category: normalize(history.category),
      start: parseApiDate(history.travelStartDate),
      end: parseApiDate(history.travelEndDate),
      year: getYear(history),
    };
  }), [flatHistories]);

  const years = useMemo(() => {
    const counts = new Map<number, number>();
    rows.forEach(row => {
      if (row.year !== null) {
        counts.set(row.year, (counts.get(row.year) || 0) + 1);
      }
    });
    return Array.from(counts.entries()).sort((a, b) => b[0] - a[0]);
  }, [rows]);

  const shown = useMemo(() => rows
    .filter(row => year === 'all' || row.year === year)
    .filter(row => !product || row.category === product), [rows, year, product]);

  const groups = useMemo<IYearGroup[]>(() => {
    const byYear = new Map<number | null, ITripRow[]>();
    shown.forEach(row => byYear.set(row.year, [...(byYear.get(row.year) || []), row]));
    return Array.from(byYear.entries())
      // Newest year first; rows without any usable date go last.
      .sort((a, b) => (b[0] ?? Number.NEGATIVE_INFINITY) - (a[0] ?? Number.NEGATIVE_INFINITY))
      .map(([groupYear, groupRows]) => ({
        year: groupYear,
        rows: [...groupRows].sort((a, b) => (b.start?.getTime() || 0) - (a.start?.getTime() || 0)),
      }));
  }, [shown]);

  const formatDates = (start: Date | null, end: Date | null): string => {
    if (!start) {
      return '—';
    }
    const short = (date: Date) => intl.formatDate(date, { day: 'numeric', month: 'short' });
    return end && end.getTime() !== start.getTime() ? `${short(start)} – ${short(end)}` : short(start);
  };

  const getNights = (row: ITripRow): string => {
    const nights = row.start && row.end ? Math.round((row.end.getTime() - row.start.getTime()) / DAY_MS) : 0;
    return nights > 0 && row.category !== 'activity'
      ? intl.formatMessage({ id: 'my.account.hub.history.nights' }, { count: nights })
      : '—';
  };

  const isYearOpen = (groupYear: number | null, index: number): boolean => {
    if (year !== 'all') {
      return true;
    }
    const override = toggledYears[String(groupYear)];
    return override ?? index < OPEN_YEARS_BY_DEFAULT;
  };

  const renderStatus = (status: string) => (status ? (
    <span className={`${styles.tripStatus} ${styles[`tripStatus_${getStatusTone(status)}`]}`}>{status}</span>
  ) : null);

  const renderRow = (row: ITripRow) => {
    const { history } = row;
    const typeId = CATEGORY_ICONS[row.category] ? `my.account.hub.quotes.type.${row.category}` : '';

    return (
      <tr key={row.key}>
        <td className={styles.cellDates}>{formatDates(row.start, row.end)}</td>
        <td className={styles.cellType}>
          <FontAwesomeIcon icon={CATEGORY_ICONS[row.category] || faSuitcase} aria-hidden="true" />
          {typeId ? intl.formatMessage({ id: typeId }) : history.category || '—'}
        </td>
        <td className={styles.cellNights}>{getNights(row)}</td>
        <td className={styles.cellCode}>{history.confirmationCode || '—'}</td>
        <td className={styles.cellStatus}>{renderStatus(history.status)}</td>
      </tr>
    );
  };

  const renderTable = (id: string, groupRows: ITripRow[]) => (
    <table id={id} className={styles.ledger}>
      <thead>
        <tr>
          <th scope="col">{intl.formatMessage({ id: 'my.account.hub.history.col.dates' })}</th>
          <th scope="col">{intl.formatMessage({ id: 'my.account.hub.bookings.product_label' })}</th>
          <th scope="col">{intl.formatMessage({ id: 'my.account.hub.history.col.nights' })}</th>
          <th scope="col">{intl.formatMessage({ id: 'my.account.hub.history.col.confirmation' })}</th>
          <th scope="col">{intl.formatMessage({ id: 'my.account.hub.history.col.status' })}</th>
        </tr>
      </thead>
      <tbody>{groupRows.map(renderRow)}</tbody>
    </table>
  );

  const renderContent = () => {
    if (!requested || (loading && !histories.length)) {
      return (
        <div aria-busy="true"><Skeleton active title={false} paragraph={{ rows: 6 }} /></div>
      );
    }
    if (error && !histories.length) {
      return (
        <AccountEmptyState
          icon={faCircleExclamation}
          title={intl.formatMessage({ id: 'my.account.hub.history.error_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.history.error_body' })}
          action={{ label: intl.formatMessage({ id: 'my.account.hub.bookings.retry' }), onClick: load }}
        />
      );
    }
    if (!histories.length) {
      return (
        <AccountEmptyState
          icon={faClockRotateLeft}
          title={intl.formatMessage({ id: 'my.account.hub.history.empty_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.history.empty_body' })}
        />
      );
    }

    return (
      <>
        <div className={styles.listToolbar}>
          {years.length > 1 ? (
            <div role="tablist" aria-label={intl.formatMessage({ id: 'my.account.hub.history.years' })} className={styles.years}>
              {([['all', rows.length]] as [YearKey, number][]).concat(years).map(([value, count]) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={year === value}
                  className={year === value ? `${styles.year} ${styles.yearActive}` : styles.year}
                  onClick={() => onYearChange(value)}
                >
                  {value === 'all' ? intl.formatMessage({ id: 'my.account.hub.history.all_years' }) : value}
                  <span className={styles.yearCount}>{count}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className={filterStyles.products} role="group" aria-label={intl.formatMessage({ id: 'my.account.hub.bookings.product_label' })}>
          <span className={filterStyles.productsLabel}>{intl.formatMessage({ id: 'my.account.hub.bookings.product_label' })}</span>
          {PRODUCTS.map(item => (
            <button
              key={item.key || 'all'}
              type="button"
              aria-pressed={product === item.key}
              className={product === item.key ? `${filterStyles.pill} ${filterStyles.pillActive}` : filterStyles.pill}
              onClick={() => setProduct(item.key)}
            >
              {intl.formatMessage({ id: item.labelId })}
            </button>
          ))}
        </div>

        <p className={filterStyles.count}>
          {intl.formatMessage({ id: 'my.account.hub.history.trip_count' }, { count: shown.length })}
        </p>

        {!shown.length ? (
          <AccountEmptyState
            icon={faMagnifyingGlass}
            title={intl.formatMessage({ id: 'my.account.hub.history.no_match_title' })}
            action={{
              label: intl.formatMessage({ id: 'my.account.hub.bookings.clear_filters' }),
              onClick: () => {
                setProduct('');
                onYearChange('all');
              },
            }}
          />
        ) : null}

        <div className={styles.yearGroups}>
          {groups.map((group, index) => {
            const open = isYearOpen(group.year, index);
            const groupId = `trip-year-${group.year ?? 'undated'}`;
            return (
              <section key={groupId} className={styles.yearGroup} aria-labelledby={`${groupId}-title`}>
                <h3 id={`${groupId}-title`} className={styles.yearHeading}>
                  <button
                    type="button"
                    className={styles.yearHeader}
                    aria-expanded={open}
                    aria-controls={groupId}
                    disabled={year !== 'all'}
                    onClick={() => setToggledYears(value => ({ ...value, [String(group.year)]: !open }))}
                  >
                    <span className={styles.yearLabel}>
                      {group.year ?? intl.formatMessage({ id: 'my.account.hub.history.undated' })}
                    </span>
                    <span className={styles.yearSummary}>
                      {intl.formatMessage({ id: 'my.account.hub.history.trip_count' }, { count: group.rows.length })}
                    </span>
                    {year === 'all' ? (
                      <FontAwesomeIcon icon={faChevronDown} className={open ? styles.chevronOpen : styles.chevron} aria-hidden="true" />
                    ) : null}
                  </button>
                </h3>
                {open ? renderTable(groupId, group.rows) : null}
              </section>
            );
          })}
        </div>
      </>
    );
  };

  return renderContent();
};
